import cn from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router-dom';

import { ColumnVisibility } from '@/components/common/ColumnVisibility/ColumnVisibility';
import { useColumnVisibility } from '@/components/common/ColumnVisibility/useColumnVisibility';
import { advanceFiltersNormalizer } from '@/helpers/advanceFilters/advanceFiltersNormalizer.helper';
import {
  filterCampaignPlansByAdvertiser,
  reconcileCascadingAdvanceFilters
} from '@/helpers/campaignFilters/cascadingFilters.helper';
import { useModals } from '@/router';
import { CampaignsStore, useStore as useCampaignStore } from '@/store/local/campaigns';
import { AggregatedReport } from '../AggregatedReport';
import EntityTabEmptyState from '../EntityTabEmptyState/EntityTabEmptyState';
import { InsertionOrdersList } from '../InsertionOrdersList';
import {
  INSERTION_ORDERS_COLUMN_VISIBILITY_KEY,
  INSERTION_ORDERS_HIDEABLE_COLUMN_IDS,
  useInsertionOrdersColumnLabels
} from '../InsertionOrdersList/insertionOrdersColumns';
import { ListFilters } from '../ListFilters';
import { Advertiser } from '../ListFilters/Advertiser';
import { CampaignPlan } from '../ListFilters/CampaignPlan';
import { DateRange } from '../ListFilters/DateRange';
import { SearchByFilters } from '../ListFilters/SearchByFilters';
import { INSERTION_ORDER_FILTER_GROUPS } from '../ListFilters/SearchByFilters/SearchByFilters';
import { Status } from '../ListFilters/Status';
import { getColumnVisibilityOptions } from '../Shared/columnVisibilityOptions';

import type { SearchByFilterEntry } from '../ListFilters/SearchByFilters';

import './insertionOrderTab.scss';

export default function InsertionOrderTab() {
  const { applyVisibleColumns, isVisible: isColumnVisible } = useColumnVisibility(
    INSERTION_ORDERS_COLUMN_VISIBILITY_KEY,
    INSERTION_ORDERS_HIDEABLE_COLUMN_IDS
  );
  const columnLabels = useInsertionOrdersColumnLabels();
  const {
    advertisers,
    cacheSeed,
    campaigns,
    hasInsertionOrders,
    ioListFilters,
    isLoadingCountLineItems,
    isLoadingGetAdvertisers,
    isLoadingGetCampaigns,
    isLoadingGetInsertionOrders,
    selectedIos,
    setCacheSeed,
    setIoListFilters,
    setSelectedIos,
    setShowSearchByFilters,
    showSearchByFilters
  } = useCampaignStore() as CampaignsStore;

  const isLoading =
    isLoadingCountLineItems || isLoadingGetAdvertisers || isLoadingGetCampaigns || isLoadingGetInsertionOrders;

  const { formatMessage } = useIntl();
  const modals = useModals();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const cpId = searchParams.get('cpId');
  const advertiserIdParam = searchParams.get('advertiserId');
  const [drillDownResyncKey, setDrillDownResyncKey] = useState(0);
  const hasAppliedDrillDown = useRef(false);

  const internalCacheSeed = cacheSeed + JSON.stringify(ioListFilters);
  const statusFilter = ioListFilters.advanceFilters?.find((el: Record<string, any>) => el.id === 'status');
  const datesFilter = ioListFilters.advanceFilters?.find((el: Record<string, any>) => el.id === 'dates');
  const campaignPlansFilter = ioListFilters.advanceFilters?.find((el: Record<string, any>) => {
    return el.id === 'campaignPlans';
  });
  const advertiserFilter = ioListFilters.advanceFilters?.find((el: Record<string, any>) => {
    return el.id === 'advertiserIds';
  });
  const filteredCampaigns = filterCampaignPlansByAdvertiser(campaigns, advertiserFilter?.value);

  useEffect(() => {
    if (hasAppliedDrillDown.current || (!cpId && !advertiserIdParam)) {
      return;
    }
    hasAppliedDrillDown.current = true;
    setIoListFilters({
      advanceFilters: [
        ...(cpId ? [{ id: 'campaignPlans', value: [cpId] }] : []),
        ...(advertiserIdParam ? [{ id: 'advertiserIds', value: [advertiserIdParam] }] : [])
      ]
    });
    setDrillDownResyncKey(prevKey => prevKey + 1);
    setSearchParams(
      prevParams => {
        prevParams.delete('cpId');
        prevParams.delete('advertiserId');
        return prevParams;
      },
      { replace: true }
    );
  }, [cpId, advertiserIdParam]);

  useEffect(() => {
    return () => {
      setSelectedIos([]);
    };
  }, []);

  const getFiltersByOrigin = (origin: 'advance' | 'searchBy'): SearchByFilterEntry[] => {
    const advanceFilters = ioListFilters.advanceFilters || [];
    if (origin === 'advance') {
      return advanceFilters.filter((f: Record<string, any>) => !INSERTION_ORDER_FILTER_GROUPS.includes(f.id));
    }
    return advanceFilters.filter((f: Record<string, any>) => INSERTION_ORDER_FILTER_GROUPS.includes(f.id));
  };

  const leftFilters = [
    {
      className: 'campaignsListsFilters__leftFiltersSelectorSmall',
      component: <Advertiser advertisers={advertisers} id="ioTabAdvertiserFilter" value={advertiserFilter?.value} />,
      id: 'campaignPlanFilters__advertiserFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersSelectorSmall',
      component: <CampaignPlan campaigns={filteredCampaigns} id="ioTabCPFilter" value={campaignPlansFilter?.value} />,
      id: 'campaignPlanFilters__campaignPlansFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersStatus',
      component: <Status value={statusFilter?.value} />,
      id: 'campaignPlanFilters__statusFilter',
      sendCallback: true
    },
    {
      className: 'campaignsListsFilters__leftFiltersDateRange',
      component: <DateRange id="tab_insertionOrder_date_filter" value={datesFilter?.value} />,
      id: 'campaignPlanFilters__dateRangeFilter',
      sendCallback: true
    }
  ];

  const handleFilter = (newFilters: Record<string, any>, isSearchByFiltersChange = false) => {
    if (!isSearchByFiltersChange) {
      setIoListFilters({
        ...newFilters,
        advanceFilters: reconcileCascadingAdvanceFilters(
          [...newFilters.advanceFilters, ...getFiltersByOrigin('searchBy')],
          campaigns
        )
      });
    } else {
      setIoListFilters({
        ...ioListFilters,
        advanceFilters: reconcileCascadingAdvanceFilters(
          [...getFiltersByOrigin('advance'), ...newFilters.advanceFilters],
          campaigns
        )
      });
    }
    setCacheSeed();
  };

  const handleReset = () => {
    setIoListFilters({});
    setCacheSeed();
  };

  const handleListChange = () => {
    setCacheSeed();
  };

  const handleListSelectionChange = setSelectedIos;

  const handleSearchByFiltersChange = (newFilters: SearchByFilterEntry[]) => {
    handleFilter(
      {
        ...ioListFilters,
        advanceFilters: newFilters
      },
      true
    );
  };

  const normalizedFilters = useMemo(() => {
    let filters: Record<string, any> = {};
    if (ioListFilters.text) {
      filters.name = encodeURIComponent(ioListFilters.text);
    }
    filters = advanceFiltersNormalizer(ioListFilters.advanceFilters, filters);
    return filters;
  }, [ioListFilters.text, ioListFilters.advanceFilters]);

  if (!hasInsertionOrders && !isLoading) {
    return (
      <EntityTabEmptyState
        buttonProps={{
          onClick: () =>
            modals.open('/campaigns/new/insertionOrderModal', {
              at: { pathname: '/campaigns', search: location.search },
              state: { modalId: 'insertionOrder-modal' }
            }),
          text: formatMessage({
            defaultMessage: 'Add Insertion Order',
            id: 'insertionOrders.emptyState.createButton'
          })
        }}
        description={formatMessage({
          defaultMessage: 'Create your first Insertion Order',
          id: 'insertionOrders.emptyState.description'
        })}
        title={formatMessage({
          defaultMessage: 'Get Started Today!',
          id: 'insertionOrders.emptyState.title'
        })}
      />
    );
  }

  return (
    <div className={cn('insertionOrderTabContent', { 'insertionOrderTabContent--withFilters': showSearchByFilters })}>
      <div className="insertionOrderTabContent__main">
        <ListFilters
          extraActions={
            <ColumnVisibility
              columns={getColumnVisibilityOptions(columnLabels, INSERTION_ORDERS_HIDEABLE_COLUMN_IDS, isColumnVisible)}
              onApply={applyVisibleColumns}
              prefixId="insertionOrderTab"
            />
          }
          forceResyncKey={drillDownResyncKey}
          initialFilters={ioListFilters}
          leftFilters={leftFilters}
          onFilter={handleFilter}
          onReset={handleReset}
          onShowFiltersClick={setShowSearchByFilters}
          prefixId="insertionOrderTab"
          searchByFilters={getFiltersByOrigin('searchBy')}
        />
        <AggregatedReport selectedEntities={selectedIos} />
        <InsertionOrdersList
          cacheSeed={internalCacheSeed}
          filters={normalizedFilters}
          isColumnVisible={isColumnVisible}
          // onClearFilters={handleReset} TODO: use this when the empty state in the list supports an action
          onListChange={handleListChange}
          onSelectionChange={handleListSelectionChange}
        />
      </div>
      {showSearchByFilters ? (
        <div className="insertionOrderTabContent__filters">
          <SearchByFilters
            onClose={() => setShowSearchByFilters(false)}
            onFiltersChange={handleSearchByFiltersChange}
            value={getFiltersByOrigin('searchBy')}
            variant="insertionOrder"
          />
        </div>
      ) : null}
    </div>
  );
}
